import { createSlice, createSelector } from '@reduxjs/toolkit';
import { logout } from "../common/reducers/initContext";
import { dataHookFactory, initMapFactory } from '../common/reducers/utilities';
import { ensureArray } from '../common/utils/arrayUtilities';

const compareNote = (n1, n2) => n1.id === n2.id;

const crewNoteSlice = createSlice({
    name: 'crewNote',
    initialState: {
        data: {}
    },
    reducers: {
        set: (state, action) => {
            let data = action.payload;
            if (!Array.isArray(data)) {
                data = [data];
            }
            data.forEach(dt => {
                delete state.data[dt.callsign];
            });
            data.forEach(dt => {
                const callsign = dt.callsign;
                if (!state.data[callsign]) {
                    state.data[callsign] = []
                }
                state.data[callsign].push(dt);
            });
        },
        addOrUpdate: (state, action) => {
            for (const note of ensureArray(action.payload)) {
                const callsign = note.callsign;
                if (state.data[callsign]) {
                    let idx = state.data[callsign].findIndex(a => compareNote(a, note));
                    if (idx >= 0) {
                        state.data[callsign].splice(idx, 1, note);
                    } else {
                        state.data[callsign].push(note);
                    }
                } else {
                    state.data[callsign] = [note];
                }
            }
        },
        remove: (state, action) => {
            for (const note of ensureArray(action.payload)) {
                const callsign = note.callsign;
                if (state.data[callsign]) {
                    let idx = state.data[callsign].findIndex(a => compareNote(a, note));
                    if (idx >= 0) {
                        state.data[callsign].splice(idx, 1);
                    }
                }
            }
        }
    },
    extraReducers: builder => {
        builder.addCase(logout, () => crewNoteSlice.getInitialState());
    }
});

// #region network operations
const fetchCrewNote = callsign => network => network.get('/api/v1/crew/' + encodeURIComponent(callsign) + '/notes', crewNoteSlice.actions.set);
export const createNote = (network, note) => network.post('/api/v1/crewnote', { ...note, autogenerated: note.autogenerated === true || note.autogenerated === '1' || note.autogenerated === 1 ? 1 : 0 }, crewNoteSlice.actions.addOrUpdate);
export const strikeNote = (network, note) => network.put('/api/v1/crewnote', note, crewNoteSlice.actions.addOrUpdate);
// #endregion

// #region selectors
const selectNote = state => state.crewNote.data;

// we now make a reselect to memoize a select function parameterized by callsign and expose that factory through a convenience function
const selectNoteByJobID = createSelector([selectNote, (note, callsign) => callsign], (note, callsign) => {
    return note[callsign] ?? []
});
const selectCrewNote = callsign => state => selectNoteByJobID(state, callsign);
// #endregion

// #region listeners
const listen = {
    crewNoteCreated: (note, { dispatch }) => dispatch(crewNoteSlice.actions.addOrUpdate(note)),
    crewNoteStruck: (note, { dispatch }) => dispatch(crewNoteSlice.actions.addOrUpdate(note))
}
// #endregion
const options = { cleanup: { root: 'crewNote', all: state => Object.values(state.crewNote.data).flat(), remove: crewNoteSlice.actions.remove } };
export const [useCrewNotes] = dataHookFactory(selectCrewNote, initMapFactory(fetchCrewNote, listen), options);

const reducerDetails = { name: crewNoteSlice.name, reducer: crewNoteSlice.reducer };
export default reducerDetails;